import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';

/**
 * Bulk material usage — one site activity, several materials consumed.
 *
 * "2nd floor slab: 40 bags cement, 2 tonnes sand, 500 kg steel" is one thing
 * that happened. The date, the activity and the remarks are shared; only the
 * materials differ.
 *
 * Written by a single database function so it is one transaction — the same
 * reasoning as `purchase-bills.ts`. A dropped connection halfway through must
 * not leave an activity holding two of its four materials.
 *
 * There is no money here, which is the whole difference from a purchase bill:
 * usage records what left the stock, not what it cost.
 */

export interface UsageLine {
  material_id: string;
  unit_id: string;
  /** Kept as a string so a half-typed "1." does not become NaN mid-keystroke. */
  quantity: string;
  remarks: string;
}

export const emptyUsageLine = (): UsageLine => ({
  material_id: '', unit_id: '', quantity: '', remarks: '',
});

export interface UsageDraft {
  date: string;
  activity: string;
  remarks: string;
  lines: UsageLine[];
}

/** True once a line has enough in it to be worth saving. */
export function usageLineFilled(l: UsageLine): boolean {
  const qty = Number(l.quantity);
  return !!l.material_id && !!l.quantity.trim() && Number.isFinite(qty) && qty > 0;
}

export function filledUsageCount(lines: UsageLine[]): number {
  return lines.filter(usageLineFilled).length;
}

/* ------------------------------ validation ------------------------------ */

export interface UsageErrors {
  date?: string;
  /** Keyed by line index. */
  lines: Record<number, Partial<Record<keyof UsageLine, string>>>;
  /** Something wrong with the entry as a whole. */
  form?: string;
}

export function validateUsage(draft: UsageDraft): UsageErrors {
  const e: UsageErrors = { lines: {} };
  if (!draft.date) e.date = 'Enter the date';

  const touchedRows = draft.lines.filter((l) => l.material_id || l.quantity.trim());
  if (!touchedRows.length) e.form = 'Add at least one material';

  const seen = new Map<string, number>();
  draft.lines.forEach((l, i) => {
    // A blank spare row is not an error — three are offered by default.
    if (!l.material_id && !l.quantity.trim()) return;
    const le: Partial<Record<keyof UsageLine, string>> = {};

    if (!l.material_id) le.material_id = 'Choose a material';
    else {
      // The same material twice in one activity is almost always a slip, and
      // accepting it quietly makes the stock figure hard to argue with later.
      const first = seen.get(l.material_id);
      if (first !== undefined) le.material_id = `Same as line ${first + 1}`;
      else seen.set(l.material_id, i);
    }

    const qty = Number(l.quantity);
    if (!l.quantity.trim() || !Number.isFinite(qty) || qty <= 0) le.quantity = 'Enter a quantity';

    if (Object.keys(le).length) e.lines[i] = le;
  });

  return e;
}

export function usageHasErrors(e: UsageErrors): boolean {
  return !!(e.date || e.form || Object.keys(e.lines).length);
}

/* -------------------------------- writing -------------------------------- */

export function useCreateUsageBatch(projectId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (draft: UsageDraft): Promise<string> => {
      const errors = validateUsage(draft);
      if (usageHasErrors(errors)) throw new Error(errors.form ?? 'Check the highlighted fields');

      const lines = draft.lines.filter(usageLineFilled).map((l) => ({
        material_id: l.material_id,
        unit_id: l.unit_id || null,
        quantity: Number(l.quantity),
        remarks: l.remarks.trim() || null,
      }));

      const { data, error } = await supabase.rpc('create_material_usage_batch', {
        p_project_id: projectId,
        p_used_on: draft.date,
        p_lines: lines,
        // undefined, not null: these carry SQL defaults, and omitting an
        // argument is how PostgREST asks for the default.
        p_activity: draft.activity.trim() || undefined,
        p_remarks: draft.remarks.trim() || undefined,
      });
      if (error) throw new Error(error.message);
      return data as string;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project-stock-map'] });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['my-submissions'] });
      void qc.invalidateQueries({ queryKey: ['materials'] });
      void qc.invalidateQueries({ queryKey: ['material'] });
      void qc.invalidateQueries({ queryKey: ['material-full-report'] });
      void qc.invalidateQueries({ queryKey: ['material-report-picker'] });
      void qc.invalidateQueries({ queryKey: ['report'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['staff-dashboard'] });
    },
  });
}
